// src/pages/Facilities.js
import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../common/Navbar';
import Footer from '../common/Footer';
import contactBg from '../assets/contact-bg3.png';

import centerTable from '../assets/facilities/Wooden Center Table.png';
import loungeChair from '../assets/facilities/Wooden Outdoor Lounge Chair.png';
import saunaBed from '../assets/facilities/Wooden Steam Sauna Bed.png';

import steamChamberLayingType from '../assets/facilities/steam chamber laying type.png';
import premiumWoodSteambox from '../assets/facilities/premium wood steambox.png';
import royalShirodharaTable from '../assets/facilities/Royal Shirodhara Table.png';
import compactSteamChamber from '../assets/facilities/compact steam chamber.png';
import royalMassageBed from '../assets/facilities/royalmassagebed.png';
import examinationBed from '../assets/facilities/examination bed.png';
import nadiSwedanaYantra from '../assets/facilities/Nadi Swedana Yantra.png';

const facilities = [
  {
    title: 'Wooden Steam Sauna Bed',
    image: saunaBed,
    description:
      'Ayusanjivni brings traditional steam therapy to life with this handcrafted wooden sauna bed. Designed for authentic Ayurvedic steam sessions, it reflects the Ayusanjivni commitment to blending age-old healing rituals with skilled woodwork.',
  },
  {
    title: 'Classic Steam Chamber',
    image: steamChamberLayingType,
    description:
      'The Classic Steam Chamber is thoughtfully designed to provide a traditional steam therapy experience in a comfortable wooden enclosure. Crafted for Ayurvedic wellness treatments, it allows gentle steam to surround the body while maintaining a calm and relaxing treatment environment.',
  },
  {
    title: 'Premium Wood Steambox',
    image: premiumWoodSteambox,
    description:
      'The Premium Wood Steambox combines traditional Ayurvedic steam therapy with refined wooden craftsmanship. Its enclosed design creates a comfortable and focused steam experience, making it a practical addition to wellness and treatment spaces at Ayusanjivni.',
  },
  {
    title: 'Compact Steam Chamber',
    image: compactSteamChamber,
    description:
      'The Compact Steam Chamber offers a practical and space-efficient solution for traditional steam therapy. Its compact wooden design is ideal for treatment environments where comfort, functionality and efficient use of space are equally important.',
  },
  {
    title: 'Royal Shirodhara Table',
    image: royalShirodharaTable,
    description:
      'The Royal Shirodhara Table is designed to support the traditional Shirodhara therapy experience. With its elegant wooden construction and thoughtfully designed treatment surface, it brings together comfort, functionality and the timeless character of Ayurvedic wellness.',
  },
  {
    title: 'Wooden Outdoor Lounge Chair',
    image: loungeChair,
    description:
      'Relaxation is at the heart of the Ayusanjivni experience. This wooden outdoor lounge chair invites guests at Ayusanjivni to unwind in nature, offering comfort and warmth that reflect the Ayusanjivni philosophy of healing with ancient roots.',
  },
  {
    title: 'Royal Massage Bed',
    image: royalMassageBed,
    description:
      'The Royal Massage Bed is thoughtfully crafted to provide a comfortable and elegant setting for traditional Ayurvedic massage therapies. Its sturdy wooden construction and spacious treatment surface create a calm and supportive environment for restorative wellness treatments at Ayusanjivni.',
  },
  {
    title: 'Wooden Center Table',
    image: centerTable,
    description:
      'At Ayusanjivni, every therapy begins on a foundation of craftsmanship. Our handcrafted wooden center table is used across Ayusanjivni treatment rooms, blending traditional design with the calm, grounded atmosphere Ayusanjivni is known for.',
  },
  {
    title: 'Examination Bed',
    image: examinationBed,
    description:
      'The Examination Bed is designed to provide a comfortable and supportive surface for patient examination and consultation. Its practical construction makes it suitable for Ayurvedic healthcare environments while maintaining the clean, calm and professional atmosphere of Ayusanjivni treatment spaces.',
  },
  {
    title: 'Nadi Swedana Yantra',
    image: nadiSwedanaYantra,
    description:
      'The Nadi Swedana Yantra is designed for traditional Ayurvedic steam therapy using focused herbal steam. Its functional design supports targeted steam treatment while complementing the traditional wellness practices and therapeutic environment at Ayusanjivni.',
  },
];

export default function Facilities() {
  const facilityRefs = useRef([]);

  useEffect(() => {
    window.scrollTo(0, 0);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('facility-visible');
          }
        });
      },
      {
        threshold: 0.15,
        rootMargin: '0px 0px -80px 0px',
      }
    );

    facilityRefs.current.forEach((element) => {
      if (element) observer.observe(element);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-white font-poppins overflow-hidden">
      <Navbar />

      {/* =========================================================
          HERO BANNER
      ========================================================= */}
      <section
        className="relative bg-cover bg-center py-28 md:py-40 text-white flex items-center justify-center overflow-hidden"
        style={{ backgroundImage: `url(${contactBg})` }}
      >
        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-black/50"></div>

        {/* Soft Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B2A2C]/30 via-transparent to-[#0B2A2C]/60"></div>

        {/* Decorative Floating Circles */}
        <div className="absolute -top-20 -left-20 w-64 h-64 rounded-full bg-white/5 blur-2xl animate-floatSlow"></div>

        <div className="absolute -bottom-32 -right-20 w-80 h-80 rounded-full bg-[#D8C49C]/10 blur-3xl animate-floatReverse"></div>

        {/* Hero Content */}
        <div className="relative z-10 text-center px-6 max-w-5xl mx-auto animate-heroReveal">
          {/* Small Label */}
          <div className="flex items-center justify-center gap-4 mb-6">
            <span className="w-12 h-px bg-[#E5D8C2]"></span>

            <span className="uppercase tracking-[0.35em] text-sm md:text-base text-[#E5D8C2]">
              Ayusanjivni
            </span>

            <span className="w-12 h-px bg-[#E5D8C2]"></span>
          </div>

          <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold mb-6 font-joan tracking-wide">
            Our Facilities
          </h1>

          <p className="text-lg md:text-2xl mb-10 font-light leading-relaxed max-w-3xl mx-auto text-white/90">
            Discover the handcrafted wooden furnishings and traditional
            wellness facilities that bring comfort and tradition to every
            Ayusanjivni space.
          </p>

          <Link
            to="/"
            className="group inline-flex items-center gap-3 px-8 py-4 bg-[#0B2A2C]/90 backdrop-blur-sm text-white rounded-full text-lg font-semibold hover:bg-[#0B2A2C] transition-all duration-500 hover:-translate-y-1 shadow-lg hover:shadow-2xl"
          >
            <span className="transition-transform duration-300 group-hover:-translate-x-1">
              ←
            </span>

            Back to Home
          </Link>
        </div>
      </section>

      {/* =========================================================
          INTRODUCTION
      ========================================================= */}
      <section className="relative bg-[#F3ECE2] pt-16 md:pt-20 pb-8 px-4 overflow-hidden">
        {/* Background Decorations */}
        <div className="absolute top-10 left-[-100px] w-56 h-56 rounded-full bg-[#0B2A2C]/5 blur-3xl"></div>

        <div className="absolute bottom-0 right-[-100px] w-72 h-72 rounded-full bg-[#C7A76C]/10 blur-3xl"></div>

        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-3 mb-5">
            <span className="w-8 h-px bg-[#0B2A2C]/40"></span>

            <span className="text-xs md:text-sm uppercase tracking-[0.3em] text-[#0B2A2C]/70 font-semibold">
              Crafted With Care
            </span>

            <span className="w-8 h-px bg-[#0B2A2C]/40"></span>
          </div>

          <p className="text-gray-700 text-base md:text-lg leading-relaxed">
            Every element of Ayusanjivni is thoughtfully selected to create a
            peaceful environment where traditional wellness meets timeless
            craftsmanship.
          </p>
        </div>
      </section>

      {/* =========================================================
          FACILITIES SECTION
      ========================================================= */}
      <section className="relative py-16 md:py-20 bg-[#F3ECE2] px-0 sm:px-2 md:px-4 overflow-hidden">
        {/* Decorative Background Elements */}
        <div className="absolute top-[15%] -left-32 w-72 h-72 rounded-full bg-[#0B2A2C]/5 blur-3xl"></div>

        <div className="absolute top-[50%] -right-32 w-80 h-80 rounded-full bg-[#B9955B]/10 blur-3xl"></div>

        <div className="absolute bottom-[10%] -left-40 w-96 h-96 rounded-full bg-[#0B2A2C]/5 blur-3xl"></div>

        <div className="relative z-10 max-w-[1700px] mx-auto flex flex-col gap-20 md:gap-28">
          {facilities.map((item, index) => (
            <div
              key={item.title}
              ref={(element) => {
                facilityRefs.current[index] = element;
              }}
              className={`facility-card flex flex-col md:flex-row items-center gap-7 md:gap-10 lg:gap-12 ${
                index % 2 === 1 ? 'md:flex-row-reverse' : ''
              }`}
            >
              {/* =====================================================
                  IMAGE
              ===================================================== */}
              <div
                className={`facility-image-wrapper group w-full md:w-[50%] ${
                  index % 2 === 0 ? 'md:mr-auto' : 'md:ml-auto'
                }`}
              >
                <div className="relative overflow-hidden rounded-2xl shadow-xl group-hover:shadow-2xl transition-all duration-700">
                  <img
                    src={item.image}
                    alt={`${item.title} - Ayusanjivni`}
                    className="facility-image w-full h-80 sm:h-96 md:h-[400px] lg:h-[450px] object-cover transition-transform duration-1000 ease-out group-hover:scale-105"
                  />

                  {/* Image Gradient */}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0B2A2C]/35 via-transparent to-transparent opacity-60 group-hover:opacity-80 transition-opacity duration-700"></div>

                  {/* Hover Shine */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>

                  {/* Image Number */}
                  <div className="absolute top-5 left-5 w-12 h-12 rounded-full bg-white/90 backdrop-blur-md flex items-center justify-center text-[#0B2A2C] font-bold shadow-lg transition-transform duration-500 group-hover:scale-110">
                    {String(index + 1).padStart(2, '0')}
                  </div>
                </div>
              </div>

              {/* =====================================================
                  CONTENT
              ===================================================== */}
              <div className="facility-content w-full md:w-[44%] px-5 md:px-2 lg:px-4">
                <div className="flex items-center gap-3 mb-4">
                  <span className="w-10 h-px bg-[#0B2A2C]/40"></span>

                  <span className="text-xs uppercase tracking-[0.25em] text-[#0B2A2C]/60 font-semibold">
                    Facility {String(index + 1).padStart(2, '0')}
                  </span>
                </div>

                <h3 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#0B2A2C] mb-5 leading-tight font-joan">
                  {item.title}
                </h3>

                <div className="w-16 h-1 bg-[#0B2A2C] rounded-full mb-6"></div>

                <p className="text-gray-700 text-base md:text-lg leading-relaxed max-w-2xl">
                  {item.description}
                </p>

                <div className="mt-7 flex items-center gap-2 opacity-60">
                  <span className="w-2 h-2 rounded-full bg-[#0B2A2C]"></span>
                  <span className="w-8 h-px bg-[#0B2A2C]"></span>
                  <span className="w-2 h-2 rounded-full bg-[#0B2A2C]"></span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* =========================================================
          CLOSING SECTION
      ========================================================= */}
      <section className="relative py-20 md:py-28 bg-[#0B2A2C] text-white text-center overflow-hidden">
        {/* Background Glow */}
        <div className="absolute top-[-150px] left-1/2 -translate-x-1/2 w-[500px] h-[300px] bg-white/5 rounded-full blur-3xl"></div>

        <div className="absolute bottom-[-150px] left-[-100px] w-80 h-80 rounded-full bg-[#C7A76C]/10 blur-3xl"></div>

        <div className="absolute top-[-100px] right-[-100px] w-80 h-80 rounded-full bg-[#C7A76C]/10 blur-3xl"></div>

        <div className="relative z-10 max-w-3xl mx-auto px-6">
          <div className="flex items-center justify-center gap-4 mb-6">
            <span className="w-12 h-px bg-white/30"></span>

            <span className="text-xs uppercase tracking-[0.35em] text-white/60">
              Experience Ayusanjivni
            </span>

            <span className="w-12 h-px bg-white/30"></span>
          </div>

          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold font-joan mb-6">
            A Space Designed for Healing
          </h2>

          <p className="text-white/75 text-base md:text-lg leading-relaxed">
            From handcrafted furnishings and steam therapy facilities to
            peaceful Ayurvedic treatment spaces, every detail is thoughtfully
            created to support comfort, relaxation and traditional wellness.
          </p>
        </div>
      </section>

      <Footer />

      {/* =========================================================
          ANIMATION STYLES
      ========================================================= */}
      <style>{`
        /* Hero Animation */
        @keyframes heroReveal {
          0% {
            opacity: 0;
            transform: translateY(30px);
          }

          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-heroReveal {
          animation: heroReveal 1.2s ease-out forwards;
        }

        /* Slow Floating Animation */
        @keyframes floatSlow {
          0%,
          100% {
            transform: translate(0, 0);
          }

          50% {
            transform: translate(25px, 20px);
          }
        }

        .animate-floatSlow {
          animation: floatSlow 8s ease-in-out infinite;
        }

        /* Reverse Floating Animation */
        @keyframes floatReverse {
          0%,
          100% {
            transform: translate(0, 0);
          }

          50% {
            transform: translate(-25px, -20px);
          }
        }

        .animate-floatReverse {
          animation: floatReverse 10s ease-in-out infinite;
        }

        /* Facility Scroll Reveal */
        .facility-card {
          opacity: 0;
          transform: translateY(70px);
          transition:
            opacity 1s ease,
            transform 1s cubic-bezier(0.22, 1, 0.36, 1);
        }

        .facility-card.facility-visible {
          opacity: 1;
          transform: translateY(0);
        }

        /* Staggered Animation */
        .facility-card:nth-child(1) {
          transition-delay: 0.05s;
        }

        .facility-card:nth-child(2) {
          transition-delay: 0.15s;
        }

        .facility-card:nth-child(3) {
          transition-delay: 0.25s;
        }

        .facility-card:nth-child(4) {
          transition-delay: 0.35s;
        }

        .facility-card:nth-child(5) {
          transition-delay: 0.45s;
        }

        .facility-card:nth-child(6) {
          transition-delay: 0.55s;
        }

        .facility-card:nth-child(7) {
          transition-delay: 0.65s;
        }

        .facility-card:nth-child(8) {
          transition-delay: 0.75s;
        }

        .facility-card:nth-child(9) {
          transition-delay: 0.85s;
        }

        .facility-card:nth-child(10) {
          transition-delay: 0.95s;
        }

        /* Image Hover */
        .facility-image-wrapper {
          transition:
            transform 0.7s cubic-bezier(0.22, 1, 0.36, 1),
            filter 0.7s ease;
        }

        .facility-image-wrapper:hover {
          transform: translateY(-8px);
        }

        /* Content Hover */
        .facility-content {
          transition:
            transform 0.7s ease,
            opacity 0.7s ease;
        }

        .facility-card:hover .facility-content {
          transform: translateY(-4px);
        }

        /* Accessibility */
        @media (prefers-reduced-motion: reduce) {
          *,
          *::before,
          *::after {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
            scroll-behavior: auto !important;
          }

          .facility-card {
            opacity: 1;
            transform: none;
          }
        }

        /* Mobile */
        @media (max-width: 767px) {
          .facility-card {
            transform: translateY(40px);
          }

          .facility-image-wrapper:hover {
            transform: translateY(-4px);
          }
        }
      `}</style>
    </div>
  );
}